import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, exhaustMap, map, mergeMap } from "rxjs/operators";
import { AuthenticationService, CcapTokenDetails, CompaniesService, Company, CompanyDetails, CompanyLink, CompanyLinkCompany, CompanyType, PermissionsApi, User, UserDetails, UsersService } from "src/app/generated/CrossPoint.IdentityManagement.Server";
import { IDataAction } from "src/app/utils/interfaces/common.interfaces";
import { IGetCompaniesData, IGetUsersData } from "src/app/utils/interfaces/remote-maintenance.interfaces";
import { errorEventAction } from "../actions/app.actions";
import { createCcapTokensFailedAction, createCcapTokensSuccessAction, createCompanyFailedAction, createCompanySuccessAction, createUserFailedAction, createUserSuccessAction, deleteUserDetailsFailedAction, deleteUserDetailsSuccessAction, getAuthInfoForCompanyFailedAction, getAuthInfoForCompanySuccessAction, getAuthInfoForUserFailedAction, getAuthInfoForUserSuccessAction, getAuthInfoIdentityManagementFailedAction, getAuthInfoIdentityManagementSuccessAction, getCcapTokensFailedAction, getCcapTokensSuccessAction, getCompaniesFailedAction, getCompaniesSuccessAction, getCompanyByEmailFailedAction, getCompanyByEmailSuccessAction, getCompanyInfoFailedAction, getCompanyInfoSuccessAction, getDealerCompaniesFailedAction, getDealerCompaniesSuccessAction, getInstallerCompaniesFailedAction, getInstallerCompaniesSuccessAction, getLinkedInstallersFailedAction, getLinkedInstallersSuccessAction, getRetailerCompaniesFailedAction, getRetailerCompaniesSuccessAction, getUserDetailsFailedAction, getUserDetailsSuccessAction, getUserFailedAction, getUsersFailedAction, getUsersSuccessAction, getUserSuccessAction, IdentityManagementActionsTypes, linkInstallerFailedAction, linkInstallerSuccessAction, revokeCcapTokenFailedAction, revokeCcapTokenSuccessAction, unlinkInstallerFailedAction, unlinkInstallerSuccessAction, updateCompanyFailedAction, updateCompanySuccessAction, updateUserDetailsFailedAction, updateUserDetailsSuccessAction } from "../actions/identitymanagement.actions";
import { INewCcapTokens, INewCompany, INewUser, IRevokeCcapToken, IUnlinkOrLinkInstaller, IUpdateCompany, IUpdateUser } from "src/app/utils/interfaces/store.interfaces";


@Injectable()
export class IdentityManagementEffects {

  public getAuthInfoIdentityManagementEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_AUTH_INFO_IDENTITY_MANAGEMENT),
        exhaustMap(() => this.authenticationService.getPermissions()
          .pipe(
            map((permissions: PermissionsApi) => getAuthInfoIdentityManagementSuccessAction({ data: permissions.permissions })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetAuthInfoIdentityManagementErrorHasOccurred" } }), getAuthInfoIdentityManagementFailedAction())
            })
          )
        )
      )
  );

  public getDealerCompaniesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_DEALER_COMPANIES),
        mergeMap(({ data: { search, maxItems, subCompaniesOnly } }: IDataAction<IGetCompaniesData>) =>
          this.companiesService.getCompanies(search, CompanyType.Dealer, maxItems, subCompaniesOnly)
          .pipe(
            map((companies: Company[]) => getDealerCompaniesSuccessAction({ data: companies })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDealerCompaniesErrorHasOccurred" } }), getDealerCompaniesFailedAction())
            })
          )
        )
      )
  );

  public getRetailerCompaniesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_RETAILER_COMPANIES),
        mergeMap(({ data: { search, maxItems, subCompaniesOnly } }: IDataAction<IGetCompaniesData>) =>
          this.companiesService.getCompanies(search, CompanyType.Retailer, maxItems, subCompaniesOnly)
          .pipe(
            map((companies: Company[]) => getRetailerCompaniesSuccessAction({ data: companies })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetRetailerCompaniesErrorHasOccurred" } }), getRetailerCompaniesFailedAction())
            })
          )
        )
      )
  );

  public getInstallerCompaniesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_INSTALLER_COMPANIES),
        mergeMap(({ data: { search, maxItems, subCompaniesOnly } }: IDataAction<IGetCompaniesData>) =>
          this.companiesService.getCompanies(search, CompanyType.Installer, maxItems, subCompaniesOnly)
          .pipe(
            map((companies: Company[]) => getInstallerCompaniesSuccessAction({ data: companies })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetInstallerCompaniesErrorHasOccurred" } }), getInstallerCompaniesFailedAction())
            })
          )
        )
      )
  );

  public getCompaniesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_COMPANIES),
        mergeMap(({ data: { search, maxItems, subCompaniesOnly } }: IDataAction<IGetCompaniesData>) =>
          this.companiesService.getCompanies(search, null, maxItems, subCompaniesOnly)
          .pipe(
            map((companies: Company[]) => getCompaniesSuccessAction({ data: companies })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetCompaniesErrorHasOccurred" } }), getCompaniesFailedAction())
            })
          )
        )
      )
  );

  public getAuthInfoForCompanyEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_AUTH_INFO_FOR_COMPANY),
        mergeMap(({ data: companyId }: IDataAction<string>) => this.companiesService.getCompanyPermissions(companyId)
          .pipe(
            map((permissions) => getAuthInfoForCompanySuccessAction({
              data: {
                companyId: companyId,
                permissions: permissions,
              }
            })),
            catchError((error: HttpErrorResponse) => {
              return of(
                errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetAuthInfoForCompanyFailed" } }),
                getAuthInfoForCompanyFailedAction({ data: companyId })
              )
            })
          )
        )
      )
  );

  public getAuthInfoForUserEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_AUTH_INFO_FOR_USER),
        mergeMap(({ data: userId }: IDataAction<string>) => this.usersService.getUserPermissions(userId)
          .pipe(
            map((permissions) => getAuthInfoForUserSuccessAction({
              data: {
                userId: userId,
                permissions: permissions,
              }
            })),
            catchError((error: HttpErrorResponse) => {
              return of(
                errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetAuthInfoForUserFailed" } }),
                getAuthInfoForUserFailedAction({ data: userId })
              )
            })
          )
        )
      )
  );

  public getCompanyInfoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_COMPANY_INFO),
        exhaustMap(({ data: companyId }: IDataAction<string>) => this.companiesService.getCompany(companyId)
          .pipe(
            map((company: CompanyDetails) => getCompanyInfoSuccessAction({
              data: company,
            })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetCompanyInfoErrorHasOccurred" } }), getCompanyInfoFailedAction())
            })
          )
        )
      )
  );

  public createCompanyEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.CREATE_COMPANY),
        exhaustMap(({ data }: IDataAction<INewCompany>) => this.companiesService.createCompany(data.companyType, data)
          .pipe(
            map((company: CompanyDetails) => createCompanySuccessAction({ data: company })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.CreateCompanyErrorHasOccurred" } }), createCompanyFailedAction())
            })
          ))
      )
  );

  public updateCompanyEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.UPDATE_COMPANY),
        exhaustMap(({ data }: IDataAction<IUpdateCompany>) => this.companiesService.updateCompany(data.companyId, data)
          .pipe(
            map((company: CompanyDetails) => updateCompanySuccessAction({ data: company })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.UpdateCompanyInfoErrorHasOccurred" } }), updateCompanyFailedAction())
            })
          ))
      )
  );

  public getCompanyByEmailEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_COMPANY_BY_EMAIL),
        exhaustMap(({ data: email }: IDataAction<string>) => this.companiesService.getCompanyByEmail(email)
          .pipe(
            map((company: CompanyLinkCompany) => getCompanyByEmailSuccessAction({ data: company })),
            catchError((error: HttpErrorResponse) => {
              return of(getCompanyByEmailFailedAction())
            })
          )
        )
      )
  );

  public getUsersEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_USERS),
        mergeMap(({ data: { search, maxItems, companyId } }: IDataAction<IGetUsersData>) =>
          this.usersService.getUsers(search, companyId, maxItems)
          .pipe(
            map((users: User[]) => getUsersSuccessAction({ data: users })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetUsersErrorHasOccurred" } }), getUsersFailedAction())
            })
          )
        )
      )
  );

  public getUserEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_USER),
        mergeMap(({ data: userId }: IDataAction<string>) => this.usersService.getUser(userId)
          .pipe(
            map((user: User) => getUserSuccessAction({ data: user })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetUserErrorHasOccurred" } }), getUserFailedAction())
            })
          )
        )
      )
  );

  public getUserDetailsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_USER_DETAILS),
        mergeMap(({ data: userId }: IDataAction<string>) => this.usersService.getUserDetails(userId)
          .pipe(
            map((userDetails: UserDetails) => getUserDetailsSuccessAction({ data: userDetails })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetUserErrorHasOccurred" } }), getUserDetailsFailedAction())
            })
          )
        )
      )
  );

  public deleteUserDetailsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.DELETE_USER),
        mergeMap(({ data: userId }: IDataAction<string>) => this.usersService.deleteUser(userId)
          .pipe(
            map(() => deleteUserDetailsSuccessAction({ data: userId })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.DeleteUserErrorHasOccurred" } }), deleteUserDetailsFailedAction())
            })
          )
        )
      )
  );

  public createUserDetailsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.CREATE_USER),
        mergeMap(({ data: newUserDetails }: IDataAction<INewUser>) => this.usersService.createUser(newUserDetails.companyId, newUserDetails)
          .pipe(
            map((userDetails: UserDetails) => createUserSuccessAction({ data: userDetails })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.CreateUserErrorHasOccurred" } }), createUserFailedAction())
            })
          )
        )
      )
  );

  public getLinkedInstallersEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_LINKED_INSTALLERS),
        exhaustMap(({ data: companyId }: IDataAction<string>) => this.companiesService.getLinkedInstallers(companyId)
          .pipe(
            map((linkedInstallers: CompanyLink[]) => getLinkedInstallersSuccessAction({ data: linkedInstallers })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetLinkedInstallersErrorHasOccurred" } }), getLinkedInstallersFailedAction())
            })
          )
        )
      )
  );

  public unlinkInstallersEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.UNLINK_INSTALLER),
        exhaustMap(({ data: unlinkInstaller }: IDataAction<IUnlinkOrLinkInstaller>) => this.companiesService.unlinkInstaller(unlinkInstaller.companyId, unlinkInstaller.installerId)
          .pipe(
            map((linkedInstallers: CompanyLink[]) => unlinkInstallerSuccessAction({ data: linkedInstallers })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.UnlinkInstallerErrorHasOccurred" } }), unlinkInstallerFailedAction())
            })
          )
        )
      )
  );

  public linkInstallersEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.LINK_INSTALLER),
        exhaustMap(({ data: linkInstaller }: IDataAction<IUnlinkOrLinkInstaller>) => this.companiesService.linkInstaller(linkInstaller.companyId, linkInstaller.installerId, true)
          .pipe(
            map((linkedInstallers: CompanyLink[]) => linkInstallerSuccessAction({ data: linkedInstallers })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.LinkInstallerErrorHasOccurred" } }), linkInstallerFailedAction())
            })
          )
        )
      )
  );

  public updateUserDetailsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.UPDATE_USER),
        mergeMap(({ data: userDetails }: IDataAction<IUpdateUser>) => this.usersService.setUserDetails(userDetails.userId, userDetails)
          .pipe(
            map((userDetails: UserDetails) => updateUserDetailsSuccessAction({ data: userDetails })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.UpdateUserErrorHasOccurred" } }), updateUserDetailsFailedAction())
            })
          )
        )
      )
  );

  public getCcapTokensEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.GET_CCAP_TOKENS),
        exhaustMap(({ data: companyId }: IDataAction<string>) => this.companiesService.getCcapTokens(companyId)
          .pipe(
            map((tokens: CcapTokenDetails[]) => getCcapTokensSuccessAction({
              data: tokens,
            })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetCcapTokensErrorHasOccurred" } }), getCcapTokensFailedAction({ data: companyId }))
            })
          )
        )
      )
  );

  public createCcapTokensEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.CREATE_CCAP_TOKENS),
        exhaustMap(({ data: newTokens }: IDataAction<INewCcapTokens>) => this.companiesService.createCcapTokens(newTokens.companyId, newTokens)
          .pipe(
            map((tokens: CcapTokenDetails[]) => createCcapTokensSuccessAction({
              data: tokens,
            })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.CreateCcapTokensErrorHasOccurred" } }), createCcapTokensFailedAction({ data: newTokens }))
            })
          )
        )
      )
  );

  public revokeCcapTokenEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(IdentityManagementActionsTypes.REVOKE_CCAP_TOKEN),
        exhaustMap(({ data: revoke }: IDataAction<IRevokeCcapToken>) => this.companiesService.revokeCcapToken(revoke.companyId, revoke.tokenId, revoke.reason)
          .pipe(
            map((token: CcapTokenDetails) => revokeCcapTokenSuccessAction({
              data: token,
            })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.RevokeCcapTokenErrorHasOccurred" } }), revokeCcapTokenFailedAction({ data: revoke }))
            })
          )
        )
      )
  );

  constructor(
    private actions$: Actions,
    private companiesService: CompaniesService,
    private authenticationService: AuthenticationService,
    private usersService: UsersService,
  ) { }
}
